import { Box, Chip, FormControlLabel, Grid, MenuItem, Switch, TextField, Typography } from '@mui/material';
import CpfInput from 'components/masked-input/CpfInput';
import PhoneInput from 'components/masked-input/PhoneInput';
import React, { useEffect, useRef } from 'react';
import { Doctor } from 'types/doctor';
import { DoctorValidation } from '../validation/useDoctorValidation';
import KeyboardDatePicker from 'components/pickers/KeyboardDatePicker';
import CnpjInput from 'components/masked-input/CnpjInput';
import { RecordSection } from 'components/record-shell/RecordShell';
import { Specialty } from 'types/specialty';
import { asInputComponent } from 'components/masked-input/asInputComponent';

interface DoctorFormProps {
  doctor: Doctor;
  handleChange: <K extends keyof Doctor>(index: K, value: Doctor[K]) => void;
  validation: DoctorValidation;
  specialties: Specialty[];
  loadingSpecialties: boolean;
}

const DoctorForm: React.FC<DoctorFormProps> = ({
  doctor,
  handleChange,
  validation,
  specialties,
  loadingSpecialties,
}) => {
  const inputs = {
    name: useRef<HTMLInputElement>(null),
    rg: useRef<HTMLInputElement>(null),
    document_number: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    person_type: useRef<HTMLSelectElement>(null),
  };

  useEffect(() => {
    const [key] = Object.keys(validation) as [keyof typeof inputs];

    if (!key) return;

    inputs[key].current?.focus();
  }, [validation]); // eslint-disable-line

  return (
    <Grid container size={12} spacing={2} maxWidth={900}>
      <Grid size={12}>
        <Typography variant="subtitle2" fontWeight={700} color="primary">
          Identificação
        </Typography>
      </Grid>

      <Grid size={12}>
        <TextField
          fullWidth
          inputRef={inputs.name}
          label="Nome completo"
          placeholder="Digite o nome completo"
          value={doctor.name}
          onChange={e => handleChange('name', e.target.value)}
          error={!!validation.name}
          helperText={validation.name}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          select
          fullWidth
          inputRef={inputs.person_type}
          error={!!validation.person_type}
          helperText={validation.person_type}
          label="Tipo de pessoa"
          placeholder="Selecione o tipo de pessoa"
          onChange={e => handleChange('person_type', e.target.value)}
          value={doctor.person_type}
        >
          <MenuItem value="N">Física</MenuItem>
          <MenuItem value="L">Jurídica</MenuItem>
        </TextField>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          inputRef={inputs.document_number}
          error={!!validation.document_number}
          helperText={validation.document_number}
          label={doctor.person_type === 'N' ? 'CPF' : 'CNPJ'}
          placeholder={doctor.person_type === 'N' ? 'Digite o CPF' : 'Digite o CNPJ'}
          value={doctor.document_number}
          onChange={e => handleChange('document_number', e.target.value)}
          slotProps={{
            input: {
              inputComponent: doctor.person_type === 'N' ? asInputComponent(CpfInput) : asInputComponent(CnpjInput),
            },
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          inputRef={inputs.rg}
          error={!!validation.rg}
          helperText={validation.rg}
          label="RG"
          placeholder="Digite o RG"
          value={doctor.rg}
          onChange={e => handleChange('rg', e.target.value)}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          select
          fullWidth
          label="Sexo"
          placeholder="Selecione o sexo"
          value={doctor.gender}
          onChange={e => handleChange('gender', e.target.value as Doctor['gender'])}
        >
          <MenuItem value="M">Masculino</MenuItem>
          <MenuItem value="F">Feminino</MenuItem>
          <MenuItem value="O">Outro</MenuItem>
          <MenuItem value="N">Prefiro não dizer</MenuItem>
        </TextField>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          value={doctor.marital_status}
          onChange={e => handleChange('marital_status', e.target.value)}
          select
          label="Estado civil"
          placeholder="Selecione o estado civil"
        >
          <MenuItem value="S">Solteiro</MenuItem>
          <MenuItem value="C">Casado</MenuItem>
          <MenuItem value="D">Divorciado</MenuItem>
          <MenuItem value="V">Viúvo</MenuItem>
        </TextField>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <KeyboardDatePicker
          fullWidth
          disableFuture
          label="Data de nascimento"
          value={doctor.birth_date}
          onChange={date => handleChange('birth_date', date)}
        />
      </Grid>
      <Grid size={12}>
        <RecordSection title="Contato">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                inputRef={inputs.email}
                label="E-mail"
                placeholder="Digite o e-mail"
                value={doctor.email}
                onChange={e => handleChange('email', e.target.value)}
                error={!!validation.email}
                helperText={validation.email}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Telefone"
                placeholder="Digite o telefone"
                value={doctor.phone}
                onChange={e => handleChange('phone', e.target.value)}
                slotProps={{
                  input: {
                    inputComponent: asInputComponent(PhoneInput),
                  },
                }}
              />
            </Grid>
          </Grid>
        </RecordSection>
      </Grid>

      <Grid size={12}>
        <RecordSection title="Dados profissionais">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="CRM"
                placeholder="Digite o CRM"
                value={doctor.crm ?? ''}
                onChange={e => handleChange('crm', e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                select
                fullWidth
                label="Especialidades"
                value={doctor.specialties}
                disabled={loadingSpecialties}
                onChange={e => handleChange('specialties', e.target.value as unknown as string[])}
                slotProps={{
                  select: {
                    multiple: true,
                    renderValue: value => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(value as string[]).map(name => (
                          <Chip key={name} size="small" label={name} />
                        ))}
                      </Box>
                    ),
                  },
                }}
              >
                {specialties.map(specialty => (
                  <MenuItem key={specialty.id} value={specialty.name}>
                    {specialty.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </RecordSection>
      </Grid>

      <Grid size={12}>
        <FormControlLabel
          label="Ativo"
          control={
            <Switch checked={!!doctor.active} onChange={e => handleChange('active', e.target.checked ? 1 : 0)} />
          }
        />
      </Grid>
    </Grid>
  );
};

export default DoctorForm;
